"use client";

import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export type Locale = 'en' | 'de';

export const SUPPORTED_LOCALES: { code: Locale; label: string; shortLabel: string }[] = [
  { code: 'en', label: 'English', shortLabel: 'EN' },
  { code: 'de', label: 'Deutsch', shortLabel: 'DE' },
];

interface LocaleContextType {
  locale: Locale;
  setLocale: (locale: Locale) => void;
}

const LocaleContext = createContext<LocaleContextType>({
  locale: 'en',
  setLocale: () => {},
});

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>('en');
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem('votely_locale') as Locale | null;
    if (saved && SUPPORTED_LOCALES.some(l => l.code === saved)) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- one-time hydrate from localStorage on mount
      setLocaleState(saved);
    }
    setMounted(true);
  }, []);

  const setLocale = (newLocale: Locale) => {
    setLocaleState(newLocale);
    localStorage.setItem('votely_locale', newLocale);
    document.documentElement.lang = newLocale;
  };

  // Avoid hydration mismatch by rendering children only after mount
  // but still render during SSR with default locale
  if (!mounted) {
    return (
      <LocaleContext.Provider value={{ locale: 'en', setLocale }}>
        {children}
      </LocaleContext.Provider>
    );
  }

  return (
    <LocaleContext.Provider value={{ locale, setLocale }}>
      {children}
    </LocaleContext.Provider>
  );
}

export function useLocale() {
  return useContext(LocaleContext);
}
